import { Box, Typography } from "@mui/material";
import React from "react";
import { AssessmentQueryType, AssessmentStatement, CompetitiveAssessment } from "./competitiveAssessmentUtils";
import CompetitiveOpportunitiesContent from "./CompetitiveOpportunitiesContent";

const Statement: React.FC<{ item: AssessmentStatement }> = ({ item }) => (
  <Typography sx={{ color: "#242424", fontSize: 12, lineHeight: 1.65, whiteSpace: "pre-wrap" }}>
    {item.statement}
  </Typography>
);

const CompetitiveAssessmentContent: React.FC<{
  assessment: CompetitiveAssessment;
  queryType?: AssessmentQueryType;
}> = ({ assessment, queryType = "CompetitiveAssessment" }) =>
  assessment.topOpportunities !== undefined ? (
    <CompetitiveOpportunitiesContent assessment={assessment} queryType={queryType} />
  ) : (
    <>
      <Typography sx={{ color: "#242424", fontSize: 13, lineHeight: 1.65, whiteSpace: "pre-wrap" }}>
        {assessment.executiveSummary || "No summary is available."}
      </Typography>
      <Box
        sx={{
          mt: 2,
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            md: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(2, minmax(0, 1fr))",
            xl: `repeat(${queryType === "CompetitiveAssessment" ? 4 : 3}, minmax(0, 1fr))`,
            xxl: `repeat(${queryType === "CompetitiveAssessment" ? 4 : 3}, minmax(0, 1fr))`
          },
          gap: 1.5
        }}
      >
        {[
          { title: "Strengths", color: "#0F7B6C", items: assessment.strengths },
          { title: "Competitive Gaps", color: "#B7791F", items: assessment.competitiveGaps },
          { title: "Improvement Opportunities", color: "#8764B8", items: assessment.improvementOpportunities },
          ...(queryType === "CompetitiveAssessment"
            ? [
                {
                  title: "Competitor Announcements",
                  color: "#0F6CBD",
                  items: assessment.competitorAnnouncements
                }
              ]
            : [])
        ].map(({ title, color, items }) => (
          <Box
            key={title}
            sx={{
              minWidth: 0,
              overflowWrap: "anywhere",
              minHeight: 150,
              p: 1.75,
              border: "1px solid #E0E0E0",
              borderTop: `4px solid ${color}`,
              borderRadius: "8px",
              bgcolor: "#FFFFFF"
            }}
          >
            <Typography component="h3" sx={{ color: "#091F3A", fontSize: 13, fontWeight: 800 }}>
              {title}
            </Typography>
            {items.length > 0 ? (
              <Box component="ul" sx={{ mt: 1.25, mb: 0, pl: 2.25 }}>
                {items.map((item, index) => (
                  <Box component="li" key={index} sx={{ mb: 1.5, "&:last-child": { mb: 0 } }}>
                    <Statement item={item} />
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography sx={{ mt: 1.25, color: "#616161", fontSize: 12 }}>No items are available.</Typography>
            )}
          </Box>
        ))}
      </Box>
      <Box sx={{ mt: 2, p: 2, borderRadius: "8px", bgcolor: "#F2F6FC" }}>
        <Typography component="h3" sx={{ mb: 0.75, color: "#091F3A", fontSize: 13, fontWeight: 800 }}>
          Final Assessment
        </Typography>
        {assessment.finalAssessment.statement ? (
          <Statement item={assessment.finalAssessment} />
        ) : (
          <Typography sx={{ color: "#616161", fontSize: 12 }}>No final assessment is available.</Typography>
        )}
      </Box>
    </>
  );

export default CompetitiveAssessmentContent;
